import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useBusinessWorkspace, businessWorkPath } from './BusinessWorkspaceContext';
import { requestKey } from './businessRequestKey';

export const workStatus = task => task.cancellationPending ? 'Stopping…' : ({ queued: 'Queued', running: 'Working…',
  awaiting_input: 'Waiting for your reply', completed: 'Completed', stopped: 'Stopped', interrupted: 'Interrupted',
  failed: 'Could not complete this task', budget_exceeded: 'Paused at the usage limit' }[task.status] || 'Saved');
const privateError = error => [401, 403, 404].includes(error.status);
const validId = id => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id);
export const workMessageContent = message => message.role === 'assistant' && message.flags?.error && !message.content?.trim()
  ? 'The model could not complete this response. Try a follow-up or choose another model.' : message.content;

// Renderers key this session by account, workspace and task. Requests are
// invalidated on navigation, including shared-client authentication retries.
export function useBusinessWork(taskId) {
  const context = useBusinessWorkspace(), router = useRouter(), { api, workspaceId } = context;
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState(null);
  const [prompt, setPrompt] = useState(''), [model, setModel] = useState(''), [busy, setBusy] = useState(false), [retryIntent, setRetryIntent] = useState(null);
  const [action, setAction] = useState(null), [title, setTitle] = useState('');
  const alive = useRef(true), controller = useRef(new AbortController()), loadingRef = useRef(null), busyRef = useRef(false);
  const messages = useRef([]), cursor = useRef(0), denied = useRef(false), generation = useRef(0);
  const current = () => alive.current && context.current();
  const options = () => {
    const ticket = generation.current;
    return { isRequestCurrent: () => current() && ticket === generation.current, signal: controller.current.signal };
  };
  function failure(error) {
    if (!current()) return;
    setError(error.message || 'Business Work is temporarily unavailable.');
    if (privateError(error)) {
      generation.current++; controller.current.abort(); controller.current = new AbortController();
      denied.current = true; messages.current = []; cursor.current = 0;
      setData(null); setPrompt(''); setRetryIntent(null); setAction(null); setTitle('');
    }
  }
  function load() {
    if (loadingRef.current) return loadingRef.current;
    if (!current()) return Promise.resolve();
    const ticket = generation.current;
    setLoading(true);
    const pending = (async () => {
      try {
        const [workspace, balance, catalog, task] = await Promise.all([
          api.get(workspaceId, options()), api.balance(workspaceId, options()), api.workModels(workspaceId, options()),
          taskId ? api.task(workspaceId, taskId, options()) : null,
        ]);
        if (!current() || ticket !== generation.current) return;
        // Read messages after status: a completed task guarantees its final
        // history was saved, so the final poll cannot miss the last reply.
        const [page, files] = await Promise.all([
          taskId ? api.workMessages(workspaceId, taskId, { after: cursor.current, limit: 30 }, options()) : { items: [], next: null },
          taskId ? api.workArtifacts(workspaceId, taskId, options()) : { items: [] },
        ]);
        if (!current() || ticket !== generation.current) return;
        if (workspace.id !== workspaceId || task && (task.workspaceId !== workspaceId || task.id !== taskId)
          || files.items.some(file => file.workspaceId !== workspaceId || file.taskId !== taskId)) {
          const error = new Error('This task is unavailable in this workspace.'); error.status = 403; throw error;
        }
        const seen = new Set(messages.current.map(message => message.id));
        messages.current = [...messages.current, ...page.items.filter(message => !seen.has(message.id))];
        cursor.current = messages.current.at(-1)?.seq || 0;
        denied.current = false;
        setData({ workspace, balance, catalog, task, messages: messages.current, more: page.next != null, files: files.items });
        setModel(previous => catalog.models.some(item => item.slug === previous) ? previous
          : catalog.models.some(item => item.slug === task?.model) ? task.model : catalog.models[0]?.slug || '');
      } catch (error) { if (ticket === generation.current) failure(error); }
    })();
    loadingRef.current = pending;
    return pending.finally(() => {
      if (current() && loadingRef.current === pending) { loadingRef.current = null; setLoading(false); }
    });
  }
  useEffect(() => {
    alive.current = true; controller.current = new AbortController(); void load();
    return () => { alive.current = false; generation.current++; loadingRef.current = null; controller.current.abort(); };
  }, []);
  const active = !!data?.task && (data.task.cancellationPending || ['queued', 'running'].includes(data.task.status));
  useEffect(() => {
    if (!active || busy) return;
    const timer = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(timer);
  }, [active, busy]);
  async function execute(operation) {
    if (!current() || busyRef.current || denied.current) return;
    const ticket = generation.current;
    busyRef.current = true; setBusy(true); setError(null);
    try { await operation(options()); }
    catch (error) { if (ticket === generation.current) failure(error); }
    finally { if (current()) { busyRef.current = false; setBusy(false); } }
  }
  const submit = event => {
    event?.preventDefault?.();
    return execute(async request => {
      if (denied.current || !data) return;
      if (!retryIntent && (!data.catalog.available || !prompt.trim() || !data.catalog.models.some(item => item.slug === model))) return;
      if (taskId && !retryIntent && (!data.task?.canContinue || data.task.archived || !Number.isSafeInteger(data.task.generation))) return;
      const intent = retryIntent || { key: requestKey(), body: { prompt, model,
        ...(taskId ? { expectedGeneration: data.task.generation } : {}) } };
      setRetryIntent(intent);
      try {
        const task = taskId ? await api.resumeWork(workspaceId, taskId, intent.body, intent.key, request)
          : await api.submitWork(workspaceId, intent.body, intent.key, request);
        if (!request.isRequestCurrent()) return;
        if (!validId(task.id) || task.workspaceId !== workspaceId
          || taskId && (task.id !== taskId || !Number.isSafeInteger(task.generation) || task.generation <= intent.body.expectedGeneration)) {
          throw new Error('The task could not be confirmed. Recover this request.');
        }
        if (taskId) {
          // Finish an older refresh before publishing the new queued state.
          // A stale completed response must not reopen the reply composer.
          await loadingRef.current;
          if (!request.isRequestCurrent()) return;
          setData(previous => ({ ...previous, task: { ...previous.task, ...task, canContinue: false, canArchive: false,
            followUp: { generation: intent.body.expectedGeneration + 1, prompt: intent.body.prompt } } }));
        }
        setPrompt(''); setRetryIntent(null); context.changed();
        if (taskId) await load(); else router.replace(businessWorkPath(workspaceId, task.id));
      } catch (error) {
        if (current() && error.status >= 400 && error.status < 500) {
          setRetryIntent(null);
          if (taskId && error.status === 409) { await loadingRef.current; if (request.isRequestCurrent()) await load(); }
        }
        throw error;
      }
    });
  };
  const stop = () => execute(async request => {
    await api.stopWork(workspaceId, taskId, request);
    if (request.isRequestCurrent()) {
      await loadingRef.current;
      if (request.isRequestCurrent()) { await load(); if (request.isRequestCurrent()) context.changed(); }
    }
  });
  const download = (file, save) => execute(async request => {
    await save({ api, workspaceId, taskId, file, ...request });
  });
  const beginAction = next => {
    if (!current() || busyRef.current || denied.current || !data?.task || !['rename', 'archive', 'unarchive', 'delete'].includes(next)) return;
    setTitle(data.task.title); setAction(next); setError(null);
  };
  const change = () => execute(async request => {
    if (!data?.task || !action) return;
    const conversationId = data.task.conversationId;
    if (!validId(conversationId)) throw new Error('Refresh this task before making changes.');
    if (action === 'delete') {
      const result = await api.deleteChat(workspaceId, conversationId, request);
      if (!request.isRequestCurrent()) return;
      if (result.deleted !== true || result.id !== conversationId || result.workspaceId !== workspaceId) throw new Error('Deletion could not be confirmed. Please retry.');
      // Invalidate an older poll before navigating. It must never restore a
      // deleted task's replies if it finishes after the deletion response.
      generation.current++; controller.current.abort(); controller.current = new AbortController();
      denied.current = true; messages.current = []; cursor.current = 0;
      setData(null); setAction(null); setTitle(''); context.changed(); router.replace(businessWorkPath(workspaceId));
      return;
    }
    if (action === 'rename' && (!title.trim() || title.trim().length > 200)) throw new Error('Use a task title between 1 and 200 characters.');
    if (action === 'archive' && !data.task.canArchive) throw new Error('Stop this task and wait for it to finish stopping before archiving it.');
    const changes = action === 'rename' ? { title: title.trim() } : { archived: action === 'archive' };
    await api.updateChat(workspaceId, conversationId, changes, request);
    if (!request.isRequestCurrent()) return;
    setAction(null); setTitle('');
    await loadingRef.current;
    if (request.isRequestCurrent()) { await load(); if (request.isRequestCurrent()) context.changed(); }
  });
  return { data, loading, error, prompt, setPrompt, model, setModel, busy, retryIntent, active, load, submit, stop, download,
    action, setAction, title, setTitle, beginAction, change };
}
